import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  Filter,
  Heart,
  Inbox,
  LogOut,
  Mail,
  MapPin,
  MessageSquare,
  Search,
  Sparkles,
  ThumbsDown,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { useInquiries } from '@hooks/useInquiries';
import { Modal } from '@components/ui';
import { InquiryChatPanel } from '@components/InquiryChatPanel';
import { PropertyInquiry, InquiryStatus } from '../../types/inquiry.types';

// Defensive helpers: Firestore docs can arrive malformed or with a pending
// serverTimestamp (null until committed), which would otherwise crash render.
const tenantPairName = (names?: string[]): string => {
  const [a, b] = names ?? [];
  if (a && b) return `${a} & ${b}`;
  return a || b || 'Matched tenants';
};

const timeAgo = (ts?: { toDate?: () => Date }): string => {
  const date = ts?.toDate?.();
  return date ? formatDistanceToNow(date, { addSuffix: true }) : 'Recently';
};

const Inquiries: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState<PropertyInquiry | null>(null);
  const [chatInquiry, setChatInquiry] = useState<PropertyInquiry | null>(null);

  const { inquiries, loading, error, updateInquiryStatus } = useInquiries(
    currentUser?.uid,
    statusFilter === 'all' ? undefined : statusFilter
  );

  // Close the detail modal on Escape.
  useEffect(() => {
    if (!selectedInquiry) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedInquiry(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedInquiry]);

  // Filter inquiries by search term
  const filteredInquiries = useMemo(() => {
    if (!searchTerm) return inquiries;

    const search = searchTerm.toLowerCase();
    return inquiries.filter(
      (inquiry) =>
        inquiry.propertyTitle?.toLowerCase().includes(search) ||
        inquiry.tenantNames?.some((name) => name?.toLowerCase().includes(search)) ||
        inquiry.message?.toLowerCase().includes(search)
    );
  }, [inquiries, searchTerm]);

  // Group inquiries by status for stats
  const stats = useMemo(() => {
    return {
      new: inquiries.filter((i) => i.status === 'new').length,
      viewed: inquiries.filter((i) => i.status === 'viewed').length,
      responded: inquiries.filter((i) => i.status === 'responded').length,
      scheduled: inquiries.filter((i) => i.status === 'scheduled').length,
      accepted: inquiries.filter((i) => i.status === 'accepted').length,
      declined: inquiries.filter((i) => i.status === 'declined').length,
      total: inquiries.length,
    };
  }, [inquiries]);

  const handleInquiryClick = async (inquiry: PropertyInquiry) => {
    setSelectedInquiry(inquiry);
    if (inquiry.status === 'new') {
      await updateInquiryStatus(inquiry.id, 'viewed');
    }
  };

  const handleStatusChange = async (inquiryId: string, newStatus: InquiryStatus) => {
    try {
      await updateInquiryStatus(inquiryId, newStatus);
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry((prev: PropertyInquiry | null) => (prev ? { ...prev, status: newStatus } : null));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const getStatusBadge = (status: InquiryStatus) => {
    const badges: Record<InquiryStatus, { label: string; className: string; icon: any }> = {
      new: {
        label: 'New',
        className: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: Sparkles,
      },
      viewed: {
        label: 'Viewed',
        className: 'bg-purple-100 text-purple-700 border-purple-200',
        icon: Eye,
      },
      responded: {
        label: 'Responded',
        className: 'bg-indigo-100 text-indigo-700 border-indigo-200',
        icon: MessageSquare,
      },
      scheduled: {
        label: 'Scheduled',
        className: 'bg-yellow-100 text-yellow-700 border-yellow-200',
        icon: Calendar,
      },
      accepted: {
        label: 'Accepted',
        className: 'bg-green-100 text-green-700 border-green-200',
        icon: CheckCircle,
      },
      declined: {
        label: 'Declined',
        className: 'bg-gray-100 text-gray-700 border-gray-200',
        icon: ThumbsDown,
      },
    };

    const badge = badges[status];
    const Icon = badge.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${badge.className}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge.label}
      </span>
    );
  };

  const getSourceBadge = (inquiry: PropertyInquiry) => {
    if (inquiry.source === 'chat_recommendation') {
      return (
        <div className="flex items-center gap-1.5 text-sm text-green-600">
          <Sparkles className="w-4 h-4" />
          <span className="font-medium">Recommended in Chat</span>
        </div>
      );
    } else if (inquiry.source === 'landlord_invite') {
      return (
        <div className="flex items-center gap-1.5 text-sm text-purple-600">
          <Mail className="w-4 h-4" />
          <span className="font-medium">You Invited Them</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <Search className="w-4 h-4" />
          <span className="font-medium">Direct Browse</span>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-DEFAULT mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading inquiries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="text-lg font-semibold mb-2">Error loading inquiries</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-0 left-8 w-64 h-64 bg-accent-purple rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      {/* Header */}
      <div className="relative bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3 sm:gap-4 mb-6">
            <button
              onClick={() => navigate('/landlord/dashboard')}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-primary-200 to-primary-300 flex items-center justify-center shadow-lg hover:shadow-xl transition-all shrink-0"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </button>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-text-muted font-semibold">Landlord</p>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-text-primary">Tenant Inquiries</h1>
              <p className="text-text-secondary text-xs sm:text-sm mt-0.5">
                Manage and respond to property inquiries
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
            <div className="p-3 sm:p-4 rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <p className="text-xs sm:text-sm font-semibold text-text-secondary truncate pr-1">New</p>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 text-blue-600 flex items-center justify-center shadow-lg shrink-0">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-extrabold text-text-primary">{stats.new}</p>
            </div>
            <div className="p-3 sm:p-4 rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <p className="text-xs sm:text-sm font-semibold text-text-secondary truncate pr-1">Viewed</p>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-purple-100 to-purple-200 text-purple-600 flex items-center justify-center shadow-lg shrink-0">
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-extrabold text-text-primary">{stats.viewed}</p>
            </div>
            <div className="p-3 sm:p-4 rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <p className="text-xs sm:text-sm font-semibold text-text-secondary truncate pr-1">Responded</p>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-indigo-200 text-indigo-600 flex items-center justify-center shadow-lg shrink-0">
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-extrabold text-text-primary">{stats.responded}</p>
            </div>
            <div className="p-3 sm:p-4 rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <p className="text-xs sm:text-sm font-semibold text-text-secondary truncate pr-1">Scheduled</p>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-yellow-100 to-yellow-200 text-yellow-600 flex items-center justify-center shadow-lg shrink-0">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-extrabold text-text-primary">{stats.scheduled}</p>
            </div>
            <div className="p-3 sm:p-4 rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <p className="text-xs sm:text-sm font-semibold text-text-secondary truncate pr-1">Accepted</p>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-green-100 to-green-200 text-green-600 flex items-center justify-center shadow-lg shrink-0">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-extrabold text-text-primary">{stats.accepted}</p>
            </div>
            <div className="p-3 sm:p-4 rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <p className="text-xs sm:text-sm font-semibold text-text-secondary truncate pr-1">Declined</p>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600 flex items-center justify-center shadow-lg shrink-0">
                  <ThumbsDown className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-extrabold text-text-primary">{stats.declined}</p>
            </div>
            <div className="p-3 sm:p-4 rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <p className="text-xs sm:text-sm font-semibold text-text-secondary truncate pr-1">Total</p>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-primary-200 to-primary-300 text-white flex items-center justify-center shadow-lg shrink-0">
                  <Inbox className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-extrabold text-text-primary">{stats.total}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input
              type="text"
              aria-label="Search inquiries by property, tenants, or message"
              placeholder="Search by property, tenants, or message..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-200 transition-all"
            />
          </div>

          {/* Status Filter */}
          <div className="relative sm:w-48">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 pointer-events-none" />
            <select
              aria-label="Filter inquiries by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as InquiryStatus | 'all')}
              className="w-full pl-9 sm:pl-10 pr-8 sm:pr-10 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-200 appearance-none cursor-pointer transition-all"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="viewed">Viewed</option>
              <option value="responded">Responded</option>
              <option value="scheduled">Scheduled</option>
              <option value="accepted">Accepted</option>
              <option value="declined">Declined</option>
            </select>
          </div>
        </div>

        {/* Inquiries List */}
        {filteredInquiries.length === 0 ? (
          <div className="card p-8 sm:p-12 bg-white/90 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mx-auto mb-4">
              <Inbox className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-text-primary mb-2">
              {searchTerm || statusFilter !== 'all' ? 'No inquiries found' : 'No inquiries yet'}
            </h3>
            <p className="text-sm sm:text-base text-gray-600">
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Inquiries from matched tenant pairs will appear here'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
            {filteredInquiries.map((inquiry) => (
              <div
                key={inquiry.id}
                role="button"
                tabIndex={0}
                onClick={() => handleInquiryClick(inquiry)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleInquiryClick(inquiry);
                  }
                }}
                className={`card p-4 sm:p-6 cursor-pointer transition-all hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary-200 ${
                  selectedInquiry?.id === inquiry.id
                    ? 'border-2 border-primary-300 shadow-lg bg-gradient-to-br from-primary-50/50 to-white'
                    : 'bg-white/90 border border-gray-100 hover:border-primary-200'
                }`}
              >
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                      {inquiry.propertyTitle}
                    </h3>
                    {getSourceBadge(inquiry)}
                  </div>
                  <div className="shrink-0">
                    {getStatusBadge(inquiry.status)}
                  </div>
                </div>

                {/* Tenant Pair */}
                <div className="flex items-center gap-3 mb-4 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                  <Users className="w-5 h-5 text-purple-600" />
                  <div>
                    <div className="font-semibold text-gray-900">
                      {tenantPairName(inquiry.tenantNames)}
                    </div>
                    <div className="text-sm text-gray-600">Matched Pair</div>
                  </div>
                </div>

                {/* Budget */}
                <div className="flex items-center gap-2 mb-3 text-sm">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">
                    Budget: R{inquiry.budgetOverlap.min.toLocaleString()} - R
                    {inquiry.budgetOverlap.max.toLocaleString()}/mo
                  </span>
                </div>

                {/* Context Indicators */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {inquiry.viewedTogether && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium border border-blue-200">
                      <Eye className="w-3.5 h-3.5" />
                      Viewed Together
                    </span>
                  )}
                  {inquiry.bothSaved && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-pink-50 text-pink-700 rounded-full text-xs font-medium border border-pink-200">
                      <Heart className="w-3.5 h-3.5" />
                      Both Saved
                    </span>
                  )}
                </div>

                {/* Message Preview */}
                {inquiry.message && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-gray-700 line-clamp-2">{inquiry.message}</p>
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {timeAgo(inquiry.createdAt)}
                  </div>
                  {selectedInquiry?.id !== inquiry.id && (
                    <span className="text-primary-DEFAULT font-medium">Click to view details →</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedInquiry && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
          onClick={() => setSelectedInquiry(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="inquiry-modal-title"
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-br from-white to-gray-50 border-b border-gray-200 p-4 sm:p-6 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h2 id="inquiry-modal-title" className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-1 line-clamp-2">
                  {selectedInquiry.propertyTitle}
                </h2>
                {getSourceBadge(selectedInquiry)}
              </div>
              <button
                onClick={() => setSelectedInquiry(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Status */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-3">
                  Inquiry Status
                </label>
                <div className="flex flex-wrap gap-2">
                  {(['viewed', 'responded', 'scheduled', 'accepted', 'declined'] as InquiryStatus[]).map(
                    (status) => (
                      <button
                        key={status}
                        onClick={() => handleStatusChange(selectedInquiry.id, status)}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-xl border-2 font-semibold transition-all ${
                          selectedInquiry.status === status
                            ? 'border-primary-300 bg-gradient-to-br from-primary-200 to-primary-300 text-white shadow-md'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-primary-200 hover:shadow-sm'
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Tenant Pair Details */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Matched Tenant Pair
                </label>
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Users className="w-6 h-6 text-purple-600" />
                    <div>
                      <div className="font-bold text-lg text-gray-900">
                        {tenantPairName(selectedInquiry.tenantNames)}
                      </div>
                      <div className="text-sm text-gray-600">Compatible roommates</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-700">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      Budget: R{selectedInquiry.budgetOverlap.min.toLocaleString()} - R
                      {selectedInquiry.budgetOverlap.max.toLocaleString()}/mo
                    </div>
                  </div>
                </div>
              </div>

              {/* Context */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Property Discovery Context
                </label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">Viewed together in chat</span>
                    {selectedInquiry.viewedTogether ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <X className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">Both users saved property</span>
                    {selectedInquiry.bothSaved ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <X className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Message */}
              {selectedInquiry.message && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Inquiry Message
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-900 whitespace-pre-wrap">{selectedInquiry.message}</p>
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Timeline</label>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>
                      Received: {timeAgo(selectedInquiry.createdAt)}
                    </span>
                  </div>
                  {selectedInquiry.respondedAt && (
                    <div className="flex items-center gap-3 text-gray-600">
                      <MessageSquare className="w-4 h-4" />
                      <span>
                        Responded:{' '}
                        {formatDistanceToNow(selectedInquiry.respondedAt.toDate(), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => navigate(`/landlord/properties/${selectedInquiry.propertyId}`)}
                  className="flex-1 py-2.5 sm:py-3 text-sm sm:text-base bg-white border-2 border-gray-200 hover:border-primary-200 hover:bg-gray-50 text-gray-800 rounded-xl font-semibold transition-all shadow-sm"
                >
                  View Property
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setChatInquiry(selectedInquiry);
                    setSelectedInquiry(null);
                  }}
                  className="flex-1 py-2.5 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-primary-200 to-primary-300 hover:from-primary-300 hover:to-primary-400 text-white rounded-xl font-semibold transition-all shadow-md hover:shadow-lg"
                >
                  Message Tenants
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inquiry chat (3-way: landlord + matched tenant pair) */}
      <Modal
        isOpen={!!chatInquiry}
        onClose={() => setChatInquiry(null)}
        title={chatInquiry ? tenantPairName(chatInquiry.tenantNames) : 'Message tenants'}
        subtitle={chatInquiry?.propertyTitle}
        size="lg"
      >
        {chatInquiry && <InquiryChatPanel inquiryId={chatInquiry.id} className="h-[60vh]" />}
      </Modal>

      {/* Logout Button */}
      <div className="mt-8 pb-6">
        <button
          onClick={async () => {
            await logout();
            navigate('/login');
          }}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl text-gray-700 font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all duration-200 shadow-sm hover:shadow-md"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </div>
    </div>
  );
};

export default Inquiries;
